/** Global SDK configuration, applied to every client unless overridden. */
export interface SocaityConfig {
  /** API key (`sk_...`) or personal token (`tk_...`). */
  apiKey?: string;
  /**
   * Session / JWT Bearer token (e.g. Supabase access token from the frontend).
   * Takes precedence over ``apiKey`` when both are set.
   */
  accessToken?: string;
  /** Socaity inference gateway base URL (service calls, status, cancel, stream). */
  baseUrl: string;
  /** Web/meta backend base URL (catalog, jobs, analytics, …), including `/v1`. */
  webBackendUrl: string;
  /** Delay between job status polls in milliseconds. */
  pollIntervalMs: number;
  /** Per-HTTP-request timeout in milliseconds. */
  requestTimeoutMs: number;
  /** Give up waiting for a job after this many milliseconds. */
  jobTimeoutMs: number;
}

export const config: SocaityConfig = {
  apiKey: undefined,
  accessToken: undefined,
  baseUrl: 'https://api.socaity.ai/services/v1',
  webBackendUrl: 'https://webapi.socaity.ai/v1',
  pollIntervalMs: 1000,
  requestTimeoutMs: 120_000,
  jobTimeoutMs: 3_600_000,
};

export class ApiKeyError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ApiKeyError';
  }
}

/** Active credential: session token wins over API key. */
export function resolveAuthToken(): string | undefined {
  return config.accessToken || config.apiKey || undefined;
}

/** Update global configuration. Validates the API key shape when one is set. */
export function configure(patch: Partial<SocaityConfig>): void {
  if (patch.apiKey !== undefined && patch.apiKey !== null && patch.apiKey !== '') {
    // sk_/tk_ API keys, or JWT (ey…) for callers that still pass the session via apiKey.
    if (!/^(sk_|tk_|ey)/.test(patch.apiKey)) {
      throw new ApiKeyError(
        'Invalid authentication token. Use a Socaity API key from https://www.socaity.ai or a valid JWT.',
      );
    }
  }
  Object.assign(config, patch);
}
